import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft, Megaphone, Calendar, Send, CheckCircle2, XCircle, Clock,
  AlertCircle, Scale, FileText, MessageSquare, Edit, Loader2, Play,
  Pause, Users, DollarSign, Target, RefreshCw, ThumbsUp, ThumbsDown,
  RotateCcw, Share2, Facebook, Twitter, Linkedin, ExternalLink, Link2,
  ChevronRight, ArrowRight, Rocket, Eye, Plus, History, Upload, Paperclip,
  BarChart3, TrendingUp, Palette, Image, FlaskConical, Gavel, MousePointer, Archive,
  ListTodo
} from 'lucide-react';
import api, { socialMediaAPI, contentAPI, campaignAnalyticsAPI, marketingTasksAPI, usersAPI } from '../api';

// Campaign workflow stages
const WORKFLOW_STAGES = [
  { id: 'draft', label: 'Draft', icon: FileText, color: 'bg-slate-500' },
  { id: 'pending_review', label: 'Review', icon: Eye, color: 'bg-blue-500' },
  { id: 'legal_review', label: 'Legal', icon: Scale, color: 'bg-purple-500' },
  { id: 'manager_approval', label: 'Approval', icon: Users, color: 'bg-amber-500' },
  { id: 'approved', label: 'Approved', icon: CheckCircle2, color: 'bg-green-500' },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar, color: 'bg-cyan-500' },
  { id: 'live', label: 'Live', icon: Rocket, color: 'bg-emerald-500' },
];

export const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalReviews, setLegalReviews] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Workflow actions
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [runningLegalReview, setRunningLegalReview] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  
  // Revision dialog
  const [revisionDialog, setRevisionDialog] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [revisionChanges, setRevisionChanges] = useState('');
  const [revisions, setRevisions] = useState([]);
  
  // Social Media Publishing
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [socialStatus, setSocialStatus] = useState({ configured: {}, connected: {} });
  const [publishContent, setPublishContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [socialPosts, setSocialPosts] = useState([]);
  const [linkUrl, setLinkUrl] = useState('');
  
  // Status workflow
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Enhanced view state
  const [activeTab, setActiveTab] = useState('overview');
  const [linkedContent, setLinkedContent] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Task management state
  const [campaignTasks, setCampaignTasks] = useState([]);
  const [taskStats, setTaskStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    due_date: ''
  });
  const [creatingTask, setCreatingTask] = useState(false);

  useEffect(() => {
    fetchCampaignData();
    fetchSocialStatus();
    fetchCampaignTasks();
    fetchTeamMembers();
  }, [campaignId]);
  
  const fetchSocialStatus = async () => {
    try {
      const [statusRes, postsRes] = await Promise.all([
        socialMediaAPI.getStatus(),
        socialMediaAPI.getPosts({ campaign_id: campaignId })
      ]);
      setSocialStatus(statusRes.data);
      setSocialPosts(postsRes.data?.posts || []);
    } catch (error) {
      console.error('Failed to fetch social status:', error);
    }
  };
  
  const fetchCampaignTasks = async () => {
    try {
      const response = await marketingTasksAPI.getCampaignTasks(campaignId);
      setCampaignTasks(response.data.tasks || []);
      setTaskStats(response.data.stats || { total: 0, completed: 0, pending: 0 });
    } catch (error) {
      console.error('Failed to fetch campaign tasks:', error);
    }
  };
  
  const fetchTeamMembers = async () => {
    try {
      const response = await usersAPI.getTeam();
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };
  
  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      setCreatingTask(true);
      await marketingTasksAPI.create({
        ...taskForm,
        campaign_id: campaignId,
        category: 'campaign'
      });
      toast.success('Task created successfully');
      setShowTaskDialog(false);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        due_date: ''
      });
      fetchCampaignTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };
  
  const handleToggleTaskComplete = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
      await marketingTasksAPI.update(task.id, { status: newStatus });
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      fetchCampaignTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await marketingTasksAPI.delete(taskId);
      toast.success('Task deleted');
      fetchCampaignTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const fetchCampaignData = async () => {
    try {
      setLoading(true);
      const [campaignRes, reviewsRes, commentsRes, approvalsRes, revisionsRes] = await Promise.all([
        api.get(`/marketing/campaigns/${campaignId}`),
        api.get(`/marketing/legal-reviews/marketing_campaign/${campaignId}`),
        api.get(`/marketing/campaigns/${campaignId}/comments`),
        api.get('/marketing/approvals/pending'),
        api.get(`/marketing/campaigns/${campaignId}/revisions`).catch(() => ({ data: [] }))
      ]);
      
      setCampaign(campaignRes.data);
      setLegalReviews(reviewsRes.data || []);
      setComments(commentsRes.data || []);
      setRevisions(revisionsRes.data || []);
      
      // Check if there's a pending approval for this campaign for current user
      const relevantApproval = approvalsRes.data?.find(a => a.entity_id === campaignId);
      setPendingApproval(relevantApproval);
      
      // Fetch linked content from Creative Hub
      try {
        const contentRes = await contentAPI.getAll({ campaign_id: campaignId, limit: 50 });
        setLinkedContent(contentRes.data?.content || contentRes.data || []);
      } catch (e) {
        console.error('Failed to fetch linked content:', e);
        setLinkedContent([]);
      }
      
      // Fetch A/B tests linked to this campaign
      try {
        const abTestsRes = await api.get('/marketing/ab-tests', { params: { campaign_id: campaignId } });
        setAbTests(abTestsRes.data?.tests || abTestsRes.data || []);
      } catch (e) {
        console.error('Failed to fetch A/B tests:', e);
        setAbTests([]);
      }
      
      // Fetch workflow history from campaign analytics
      try {
        const historyRes = await campaignAnalyticsAPI.getWorkflowHistory(campaignId, 100);
        setWorkflowHistory(historyRes.data?.history || []);
      } catch (e) {
        console.error('Failed to fetch workflow history:', e);
        // Fallback to revisions
        setWorkflowHistory(revisionsRes.data || []);
      }
      
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
      toast.error('Failed to load campaign details');
      navigate('/marketing/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    try {
      setSubmittingForReview(true);
      await api.post(`/marketing/campaigns/${campaignId}/submit-for-review`);
      toast.success('Campaign submitted for review');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for review');
    } finally {
      setSubmittingForReview(false);
    }
  };

  const handleRunLegalReview = async () => {
    if (!campaign) return;
    
    // Prepare content for review
    const contentToReview = [
      campaign.name,
      campaign.description || '',
      ...(campaign.assets?.map(a => a.name) || [])
    ].join('\n\n');
    
    try {
      setRunningLegalReview(true);
      const res = await api.post('/marketing/legal-review', {
        entity_type: 'marketing_campaign',
        entity_id: campaignId,
        content: contentToReview,
        content_type: 'text',
        additional_context: `Campaign Type: ${campaign.campaign_type}, Channels: ${campaign.channels?.join(', ')}`
      });
      
      if (res.data.passed) {
        toast.success(`Legal review passed with ${res.data.overall_score.toFixed(1)}% score`);
      } else {
        toast.warning(`Legal review failed with ${res.data.overall_score.toFixed(1)}% score. Revisions needed.`);
      }
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to run legal review');
    } finally {
      setRunningLegalReview(false);
    }
  };

  const handleApprovalResponse = async (status) => {
    if (!pendingApproval) return;
    
    try {
      setProcessingApproval(true);
      await api.post(`/marketing/approvals/${pendingApproval.id}/respond`, {
        status,
        notes: approvalNotes
      });
      
      toast.success(`Campaign ${status}`);
      setApprovalDialog(false);
      setApprovalNotes('');
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to process approval');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Direct approve action
  const handleDirectApprove = async () => {
    try {
      setProcessingApproval(true);
      await api.post(`/marketing/campaigns/${campaignId}/approve`, null, {
        params: { notes: approvalNotes }
      });
      toast.success('Campaign approved!');
      setApprovalDialog(false);
      setApprovalNotes('');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve campaign');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Archive campaign action
  const handleArchiveCampaign = async () => {
    if (!window.confirm('Are you sure you want to archive this campaign? This will move it to the archives with all its performance data.')) {
      return;
    }
    try {
      const res = await campaignAnalyticsAPI.archiveCampaign(campaignId);
      toast.success('Campaign archived successfully!');
      navigate('/marketing/campaign-archives');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive campaign');
    }
  };

  // Direct reject action
  const handleDirectReject = async () => {
    if (!approvalNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      setProcessingApproval(true);
      await api.post(`/marketing/campaigns/${campaignId}/reject`, null, {
        params: { reason: approvalNotes }
      });
      toast.success('Campaign rejected');
      setApprovalDialog(false);
      setApprovalNotes('');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject campaign');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Request revision action
  const handleRequestRevision = async () => {
    if (!revisionReason.trim()) {
      toast.error('Please provide a reason for revision');
      return;
    }
    try {
      setProcessingApproval(true);
      const changes = revisionChanges.split('\n').filter(c => c.trim());
      await api.post(`/marketing/campaigns/${campaignId}/request-revision`, null, {
        params: { 
          reason: revisionReason,
          specific_changes: changes,
          priority: 'medium'
        }
      });
      toast.success('Revision requested');
      setRevisionDialog(false);
      setRevisionReason('');
      setRevisionChanges('');
      fetchCampaignData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Social Media Publishing
  const handleOpenPublishDialog = () => {
    // Pre-populate content from campaign
    const defaultContent = campaign?.headline || campaign?.tagline || campaign?.name || '';
    setPublishContent(defaultContent);
    setLinkUrl(campaign?.landing_page_url || '');
    setSelectedPlatforms([]);
    setShowPublishDialog(true);
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handlePublishToSocial = async () => {
    if (!publishContent.trim()) {
      toast.error('Please enter content to publish');
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    
    try {
      setPublishing(true);
      const res = await socialMediaAPI.publish({
        campaign_id: campaignId,
        platforms: selectedPlatforms,
        content: publishContent,
        link_url: linkUrl || undefined
      });
      
      const result = res.data;
      if (result.status === 'published') {
        toast.success('Published to all platforms successfully!');
      } else if (result.status === 'partial') {
        toast.warning('Published to some platforms. Check details for errors.');
      } else {
        toast.error('Publishing failed. Please try again.');
      }
      
      setShowPublishDialog(false);
      setPublishContent('');
      setSelectedPlatforms([]);
      fetchSocialStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to publish';
      toast.error(errorMsg);
    } finally {
      setPublishing(false);
    }
  };

  // Status Workflow Functions
  const getCurrentStageIndex = () => {
    return WORKFLOW_STAGES.findIndex(s => s.id === campaign?.status) || 0;
  };

  const getNextStatus = () => {
    const currentIndex = getCurrentStageIndex();
    if (currentIndex < WORKFLOW_STAGES.length - 1) {
      return WORKFLOW_STAGES[currentIndex + 1];
    }
    return null;
  };

  const canAdvanceStatus = () => {
    const status = campaign?.status;
    // Check if user has permission to advance
    if (['pending_review', 'legal_review'].includes(status)) {
      return hasRole(['admin', 'marketing_manager']);
    }
    if (status === 'manager_approval') {
      return hasRole(['admin', 'marketing_manager']);
    }
    if (status === 'approved') {
      return hasRole(['admin', 'marketing_manager']);
    }
    if (status === 'scheduled') {
      return hasRole(['admin', 'marketing_manager']);
    }
    return hasRole(['admin', 'marketing_manager']);
  };

  const handleAdvanceStatus = async () => {
    const nextStatus = getNextStatus();
    if (!nextStatus) return;
    
    try {
      setUpdatingStatus(true);
      await api.put(`/marketing/campaigns/${campaignId}`, {
        status: nextStatus.id
      });
      toast.success(`Campaign moved to ${nextStatus.label}`);
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await api.put(`/marketing/campaigns/${campaignId}`, {
        status: newStatus
      });
      const stage = WORKFLOW_STAGES.find(s => s.id === newStatus);
      toast.success(`Campaign status changed to ${stage?.label || newStatus}`);
      setShowStatusDialog(false);
      setSelectedStatus('');
      setStatusNote('');
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      await api.post(`/marketing/campaigns/${campaignId}/comments`, {
        campaign_id: campaignId,
        content: newComment
      });
      
      setNewComment('');
      toast.success('Comment added');
      fetchCampaignData();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Draft', color: 'bg-slate-500', icon: FileText },
      pending_review: { label: 'Pending Review', color: 'bg-blue-500', icon: Clock },
      legal_review: { label: 'Legal Review', color: 'bg-purple-500', icon: Scale },
      manager_approval: { label: 'Manager Approval', color: 'bg-amber-500', icon: Users },
      revision_requested: { label: 'Revision Requested', color: 'bg-orange-500', icon: RotateCcw },
      approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle2 },
      scheduled: { label: 'Scheduled', color: 'bg-cyan-500', icon: Calendar },
      live: { label: 'Live', color: 'bg-emerald-500', icon: Play },
      paused: { label: 'Paused', color: 'bg-orange-500', icon: Pause },
      completed: { label: 'Completed', color: 'bg-slate-400', icon: CheckCircle2 },
      rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle }
    };
    return configs[status] || { label: status, color: 'bg-slate-500', icon: FileText };
  };

  const getWorkflowProgress = () => {
    const stages = ['draft', 'pending_review', 'legal_review', 'manager_approval', 'approved', 'scheduled', 'live'];
    const currentIndex = stages.indexOf(campaign?.status || 'draft');
    return Math.max(0, ((currentIndex + 1) / stages.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Campaign not found</p>
        <Link to="/marketing/campaigns">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
        </Link>
      </div>
    );
  }

  const statusConfig = getStatusConfig(campaign.status);
  const StatusIcon = statusConfig.icon;
  const latestLegalReview = legalReviews[0];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link to="/marketing/campaigns">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{campaign.name}</h1>
              <Badge className={`${statusConfig.color} text-white`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>
            <p className="text-muted-foreground capitalize">
              {campaign.campaign_type?.replace('_', ' ')} Campaign
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCampaignData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && ['completed', 'live', 'rejected'].includes(campaign.status) && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleArchiveCampaign}
              data-testid="archive-campaign-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          )}
          {hasRole(['admin', 'marketing_manager']) && campaign.status === 'draft' && (
            <Link to={`/marketing/campaigns/${campaignId}/edit`}>
              <Button variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Workflow Progress - Visual Slider */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Campaign Workflow</CardTitle>
              <CardDescription>
                Current stage: {statusConfig.label}
              </CardDescription>
            </div>
            {hasRole(['admin', 'marketing_manager']) && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowStatusDialog(true)}
                  data-testid="change-status-btn"
                >
                  Change Status
                </Button>
                {getNextStatus() && canAdvanceStatus() && !['completed', 'rejected', 'live'].includes(campaign.status) && (
                  <Button 
                    size="sm"
                    onClick={handleAdvanceStatus}
                    disabled={updatingStatus}
                    data-testid="advance-status-btn"
                  >
                    {updatingStatus ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Move to {getNextStatus()?.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Visual Stage Slider */}
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-5 left-0 right-0 h-1 bg-slate-200 rounded-full" />
            <div 
              className="absolute top-5 left-0 h-1 bg-primary rounded-full transition-all duration-500"
              style={{ width: `${getWorkflowProgress()}%` }}
            />
            
            {/* Stage Nodes */}
            <div className="relative flex justify-between">
              {WORKFLOW_STAGES.map((stage, index) => {
                const currentIndex = getCurrentStageIndex();
                const isActive = index <= currentIndex;
                const isCurrent = stage.id === campaign.status;
                const StageIcon = stage.icon;
                
                return (
                  <div 
                    key={stage.id}
                    className="flex flex-col items-center"
                    data-testid={`stage-${stage.id}`}
                  >
                    <div 
                      className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCurrent 
                          ? 'bg-primary text-white ring-4 ring-primary/20 scale-110' 
                          : isActive 
                            ? 'bg-primary/80 text-white' 
                            : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      <StageIcon className="h-4 w-4" />
                    </div>
                    <span className={`mt-2 text-xs font-medium ${
                      isCurrent ? 'text-primary' : isActive ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {stage.label}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] text-primary font-semibold mt-1">Current</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t">
            {campaign.status === 'draft' && (
              <Button 
                variant="outline" 
                onClick={handleSubmitForReview}
                disabled={submittingForReview}
              >
                {submittingForReview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Submit for Review
              </Button>
            )}
            {['pending_review', 'legal_review', 'manager_approval'].includes(campaign.status) && hasRole(['admin', 'marketing_manager']) && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setRevisionDialog(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Request Changes
                </Button>
                <Button 
                  onClick={() => handleStatusChange('approved')}
                  disabled={updatingStatus}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {campaign.status === 'approved' && hasRole(['admin', 'marketing_manager']) && (
              <Button onClick={() => handleStatusChange('scheduled')}>
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Campaign
              </Button>
            )}
            {campaign.status === 'scheduled' && hasRole(['admin', 'marketing_manager']) && (
              <Button onClick={() => handleStatusChange('live')} className="bg-emerald-600 hover:bg-emerald-700">
                <Rocket className="h-4 w-4 mr-2" />
                Go Live
              </Button>
            )}
            {campaign.status === 'live' && hasRole(['admin', 'marketing_manager']) && (
              <>
                <Button variant="outline" onClick={() => handleStatusChange('paused')}>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </Button>
                <Button onClick={() => handleStatusChange('completed')}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </Button>
              </>
            )}
            {campaign.status === 'paused' && hasRole(['admin', 'marketing_manager']) && (
              <Button onClick={() => handleStatusChange('live')} className="bg-emerald-600 hover:bg-emerald-700">
                <Play className="h-4 w-4 mr-2" />
                Resume
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-background border p-1 flex-wrap">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-2">
            <ListTodo className="h-4 w-4" />
            Tasks ({taskStats.pending || 0})
          </TabsTrigger>
          <TabsTrigger value="workflow" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Content & Assets
          </TabsTrigger>
          <TabsTrigger value="testing" className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" />
            A/B Testing
          </TabsTrigger>
          <TabsTrigger value="results" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Results
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="discussion" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Discussion ({comments.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Campaign Brief */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Campaign Brief
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Description */}
                {campaign.description && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <Label className="text-muted-foreground text-xs uppercase tracking-wider">Description</Label>
                    <p className="mt-2">{campaign.description}</p>
                  </div>
                )}

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                    <DollarSign className="h-5 w-5 text-green-600 mb-2" />
                    <p className="text-xs text-green-700 uppercase">Budget</p>
                    <p className="text-xl font-bold text-green-900">${(campaign.budget || 0).toLocaleString()}</p>
                    {campaign.spent && (
                      <p className="text-xs text-green-700">{Math.round((campaign.spent / campaign.budget) * 100)}% spent</p>
                    )}
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <TrendingUp className="h-5 w-5 text-blue-600 mb-2" />
                    <p className="text-xs text-blue-700 uppercase">Expected ROI</p>
                    <p className="text-xl font-bold text-blue-900">{campaign.expected_roi || 0}%</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                    <Calendar className="h-5 w-5 text-purple-600 mb-2" />
                    <p className="text-xs text-purple-700 uppercase">Duration</p>
                    <p className="text-sm font-bold text-purple-900">
                      {campaign.start_date ? format(new Date(campaign.start_date), 'MMM d') : 'TBD'}
                      {campaign.start_date && campaign.end_date && ' - '}
                      {campaign.end_date ? format(new Date(campaign.end_date), 'MMM d') : ''}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                    <Users className="h-5 w-5 text-amber-600 mb-2" />
                    <p className="text-xs text-amber-700 uppercase">Type</p>
                    <p className="text-sm font-bold text-amber-900 capitalize">{campaign.campaign_type?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                </div>

                {/* Target Audience */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-900">
                    <Users className="h-4 w-4" />
                    Target Audience
                  </h4>
                  <div className="space-y-3">
                    {campaign.target_segments && campaign.target_segments.length > 0 && (
                      <div>
                        <Label className="text-xs text-blue-700">Segments</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {campaign.target_segments.map(seg => (
                            <Badge key={seg} className="bg-blue-100 text-blue-700">{seg}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {campaign.audience_description && (
                      <div>
                        <Label className="text-xs text-blue-700">Description</Label>
                        <p className="text-sm mt-1">{campaign.audience_description}</p>
                      </div>
                    )}
                    {(!campaign.target_segments || campaign.target_segments.length === 0) && !campaign.audience_description && (
                      <p className="text-sm text-blue-700">No target audience defined</p>
                    )}
                  </div>
                </div>

                {/* Channels & KPIs */}
                <div className="grid grid-cols-2 gap-4">
                  {campaign.channels && campaign.channels.length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-semibold mb-2 text-purple-900 text-sm">Channels</h4>
                      <div className="flex flex-wrap gap-2">
                        {campaign.channels.map(ch => (
                          <Badge key={ch} variant="outline" className="bg-white">{ch}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {campaign.kpis && campaign.kpis.length > 0 && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold mb-2 text-green-900 text-sm">KPIs & Goals</h4>
                      <ul className="space-y-1">
                        {campaign.kpis.map((kpi, idx) => (
                          <li key={idx} className="text-sm flex items-center gap-2">
                            <Target className="h-3 w-3 text-green-600" />
                            {typeof kpi === 'object' ? `${kpi.name}: ${kpi.target}` : kpi}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Info Sidebar */}
            <div className="space-y-4">
              {/* Legal Review Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Gavel className="h-4 w-4" />
                    Legal Review
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {latestLegalReview ? (
                    <div className={`p-3 rounded-lg ${latestLegalReview.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${latestLegalReview.passed ? 'text-green-700' : 'text-red-700'}`}>
                          {latestLegalReview.passed ? 'Passed' : 'Failed'}
                        </span>
                        <Badge variant={latestLegalReview.passed ? 'default' : 'destructive'}>
                          {latestLegalReview.overall_score?.toFixed(0)}%
                        </Badge>
                      </div>
                      {campaign.legal_score && (
                        <p className="text-xs mt-2 text-muted-foreground">
                          Score saved: {format(new Date(campaign.legal_review_date || campaign.updated_at), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <Scale className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No legal review yet</p>
                      {hasRole(['admin', 'marketing_manager']) && (
                        <Button variant="outline" size="sm" className="mt-2" onClick={handleRunLegalReview}>
                          Run Review
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Social Media Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Social Media
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {socialStatus.connected?.facebook && <Badge variant="outline" className="bg-blue-50"><Facebook className="h-3 w-3 mr-1" /> FB</Badge>}
                      {socialStatus.connected?.twitter && <Badge variant="outline"><Twitter className="h-3 w-3 mr-1" /> X</Badge>}
                      {socialStatus.connected?.linkedin && <Badge variant="outline" className="bg-blue-50"><Linkedin className="h-3 w-3 mr-1" /> LI</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{socialPosts.length} posts published</p>
                    {['approved', 'scheduled', 'live'].includes(campaign.status) && (
                      <Button variant="outline" size="sm" className="w-full" onClick={handleOpenPublishDialog}>
                        <Share2 className="h-3 w-3 mr-1" />
                        Publish Now
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Linked Content */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Linked Content
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {linkedContent.length > 0 ? (
                    <div className="space-y-2">
                      {linkedContent.slice(0, 3).map(content => (
                        <div key={content.id} className="p-2 bg-slate-50 rounded text-sm">
                          <p className="font-medium truncate">{content.title}</p>
                          <p className="text-xs text-muted-foreground capitalize">{content.content_type?.replace('_', ' ')}</p>
                        </div>
                      ))}
                      {linkedContent.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{linkedContent.length - 3} more</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <FileText className="h-6 w-6 mx-auto mb-1 opacity-50" />
                      <p className="text-xs">No content linked</p>
                      <Link to="/marketing/creative">
                        <Button variant="link" size="sm">Link Content</Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ListTodo className="h-5 w-5" />
                  Campaign Tasks
                </CardTitle>
                <CardDescription>
                  {taskStats.completed} of {taskStats.total} tasks completed
                </CardDescription>
              </div>
              <Button onClick={() => setShowTaskDialog(true)} data-testid="add-campaign-task-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </CardHeader>
            <CardContent>
              {taskStats.total > 0 && (
                <div className="mb-4">
                  <Progress value={(taskStats.completed / taskStats.total) * 100} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1">
                    {taskStats.completion_rate || 0}% complete
                  </p>
                </div>
              )}
              
              {campaignTasks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ListTodo className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tasks for this campaign yet</p>
                  <p className="text-sm">Create tasks to track work and assign to team members</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowTaskDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Task
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {campaignTasks.map((task) => (
                      <div 
                        key={task.id} 
                        className={`p-4 border rounded-lg ${task.status === 'completed' ? 'bg-slate-50' : 'hover:bg-slate-50'} transition-colors`}
                        data-testid={`campaign-task-${task.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox 
                            checked={task.status === 'completed'}
                            onCheckedChange={() => handleToggleTaskComplete(task)}
                            className="mt-1"
                            data-testid={`task-checkbox-${task.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                {task.title}
                              </p>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteTask(task.id)} className="h-8 w-8">
                                <XCircle className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                              </Button>
                            </div>
                            {task.description && (
                              <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <Badge className={
                                task.priority === 'critical' ? 'bg-red-500 text-white' :
                                task.priority === 'high' ? 'bg-orange-500 text-white' :
                                task.priority === 'medium' ? 'bg-yellow-500 text-white' :
                                'bg-gray-400 text-white'
                              }>
                                {task.priority}
                              </Badge>
                              {task.assigned_to_name && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {task.assigned_to_name}
                                </span>
                              )}
                              {task.due_date && (
                                <span className={`text-xs flex items-center gap-1 ${
                                  new Date(task.due_date) < new Date() && task.status !== 'completed' 
                                    ? 'text-red-500' 
                                    : 'text-muted-foreground'
                                }`}>
                                  <Clock className="h-3 w-3" />
                                  {format(new Date(task.due_date), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Tab */}
        <TabsContent value="workflow" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Workflow Actions */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Workflow Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
            {/* Draft - Submit for Review */}
            {campaign.status === 'draft' && hasRole(['admin', 'marketing_manager']) && (
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium text-blue-900">Ready to submit?</p>
                  <p className="text-sm text-blue-700">Submit this campaign for team review</p>
                </div>
                <Button onClick={handleSubmitForReview} disabled={submittingForReview}>
                  {submittingForReview ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Submit for Review
                </Button>
              </div>
            )}

            {/* Pending Review - Approve/Reject */}
            {pendingApproval && campaign.status === 'pending_review' && (
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <p className="font-medium text-amber-900">Awaiting Your Review</p>
                  <p className="text-sm text-amber-700">Review this campaign and provide your feedback</p>
                </div>
                <Button onClick={() => setApprovalDialog(true)}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Review Campaign
                </Button>
              </div>
            )}

            {/* Manager Approval Actions - Direct Approve/Reject/Revision */}
            {hasRole(['admin', 'marketing_manager']) && ['pending_review', 'manager_approval', 'legal_review'].includes(campaign.status) && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="font-medium mb-3">Manager Actions</p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={() => { setApprovalNotes(''); setApprovalDialog(true); }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setRevisionDialog(true)}
                    className="text-orange-600 border-orange-600 hover:bg-orange-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Request Revision
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => { setApprovalNotes(''); setApprovalDialog(true); }}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Legal Review */}
            {campaign.status === 'legal_review' && hasRole(['admin', 'marketing_manager']) && (
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <p className="font-medium text-purple-900">Legal Compliance Review</p>
                  <p className="text-sm text-purple-700">Run AI-powered legal review on campaign content</p>
                </div>
                <Button onClick={handleRunLegalReview} disabled={runningLegalReview}>
                  {runningLegalReview ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Scale className="h-4 w-4 mr-2" />
                  )}
                  Run Legal Review
                </Button>
              </div>
            )}

            {/* Revision Requested Alert */}
            {campaign.status === 'revision_requested' && (
              <Alert className="bg-orange-50 border-orange-200">
                <RotateCcw className="h-4 w-4 text-orange-600" />
                <AlertTitle className="text-orange-800">Revision Requested</AlertTitle>
                <AlertDescription className="text-orange-700">
                  This campaign requires revisions before it can be approved.
                  {revisions[0]?.reason && (
                    <p className="mt-1 font-medium">Reason: {revisions[0].reason}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Legal Review Results */}
            {latestLegalReview && (
              <Alert className={latestLegalReview.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {latestLegalReview.passed ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertTitle className={latestLegalReview.passed ? 'text-green-800' : 'text-red-800'}>
                  Legal Review: {latestLegalReview.passed ? 'Passed' : 'Failed'} ({latestLegalReview.overall_score?.toFixed(1)}%)
                </AlertTitle>
                <AlertDescription className={latestLegalReview.passed ? 'text-green-700' : 'text-red-700'}>
                  {latestLegalReview.suggestions?.join('. ') || 'Review completed.'}
                </AlertDescription>
              </Alert>
            )}
            
            {/* Social Media Publishing - Show for approved/live campaigns */}
            {['approved', 'scheduled', 'live'].includes(campaign.status) && hasRole(['admin', 'marketing_manager']) && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-blue-600" />
                    <p className="font-medium text-blue-900">Social Media Publishing</p>
                  </div>
                  <Button 
                    onClick={handleOpenPublishDialog}
                    data-testid="publish-social-btn"
                    disabled={!socialStatus.any_connected}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Publish to Social
                  </Button>
                </div>
                
                {/* Connected Platforms */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Connected:</span>
                  {socialStatus.connected?.facebook && (
                    <Badge variant="outline" className="bg-blue-100">
                      <Facebook className="h-3 w-3 mr-1" /> Facebook
                    </Badge>
                  )}
                  {socialStatus.connected?.twitter && (
                    <Badge variant="outline" className="bg-slate-100">
                      <Twitter className="h-3 w-3 mr-1" /> Twitter
                    </Badge>
                  )}
                  {socialStatus.connected?.linkedin && (
                    <Badge variant="outline" className="bg-blue-100">
                      <Linkedin className="h-3 w-3 mr-1" /> LinkedIn
                    </Badge>
                  )}
                  {!socialStatus.any_connected && (
                    <Link to="/settings/social-media" className="text-blue-600 hover:underline flex items-center gap-1">
                      <Link2 className="h-3 w-3" /> Connect accounts
                    </Link>
                  )}
                </div>
                
                {/* Previous Posts */}
                {socialPosts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-muted-foreground mb-2">Recent Posts ({socialPosts.length})</p>
                    <div className="space-y-2">
                      {socialPosts.slice(0, 3).map(post => (
                        <div key={post.id} className="flex items-center justify-between text-sm bg-white p-2 rounded">
                          <div className="flex items-center gap-2">
                            {post.platforms?.map(p => (
                              <Badge key={p} variant="secondary" className="text-xs">
                                {p === 'facebook' && <Facebook className="h-3 w-3 mr-1" />}
                                {p === 'twitter' && <Twitter className="h-3 w-3 mr-1" />}
                                {p === 'linkedin' && <Linkedin className="h-3 w-3 mr-1" />}
                                {p}
                              </Badge>
                            ))}
                            <span className="truncate max-w-[150px]">{post.content?.substring(0, 50)}...</span>
                          </div>
                          <Badge variant={post.status === 'published' ? 'default' : post.status === 'partial' ? 'outline' : 'destructive'}>
                            {post.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaign Sidebar Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Timeline */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-muted-foreground text-xs">Timeline</Label>
              <p className="text-sm font-medium">
                {campaign.start_date ? format(new Date(campaign.start_date), 'MMM d, yyyy') : 'TBD'}
                {campaign.start_date && campaign.end_date && ' → '}
                {campaign.end_date ? format(new Date(campaign.end_date), 'MMM d, yyyy') : ''}
              </p>
            </div>
            
            {/* Budget Summary */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <Label className="text-green-700 text-xs">Budget</Label>
              <p className="text-lg font-bold text-green-900">${(campaign.budget || 0).toLocaleString()}</p>
              {campaign.spent && (
                <Progress value={(campaign.spent / campaign.budget) * 100} className="h-1 mt-2" />
              )}
            </div>
          </CardContent>
        </Card>
          </div>
        </TabsContent>

        {/* Content & Assets Tab */}
        <TabsContent value="content" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Creative Content */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Creative Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign.headline && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="text-xs text-amber-700 uppercase">Headline</Label>
                    <p className="text-lg font-bold mt-1">{campaign.headline}</p>
                  </div>
                )}
                {campaign.tagline && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-muted-foreground uppercase">Tagline</Label>
                    <p className="mt-1 italic">{campaign.tagline}</p>
                  </div>
                )}
                {campaign.body_copy && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-muted-foreground uppercase">Body Copy</Label>
                    <p className="mt-1 text-sm whitespace-pre-wrap">{campaign.body_copy}</p>
                  </div>
                )}
                {campaign.call_to_action && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">CTA:</Label>
                    <Badge className="bg-primary">{campaign.call_to_action}</Badge>
                  </div>
                )}
                {campaign.landing_page_url && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Landing Page</Label>
                    <a href={campaign.landing_page_url} target="_blank" rel="noopener noreferrer" 
                       className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                      <ExternalLink className="h-3 w-3" />
                      {campaign.landing_page_url}
                    </a>
                  </div>
                )}
                {!campaign.headline && !campaign.tagline && !campaign.body_copy && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No creative content added yet</p>
                    <Link to={`/marketing/campaigns/${campaignId}/edit`}>
                      <Button variant="link">Add Content</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Linked Content from Creative Hub */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Linked Content
                </CardTitle>
                <CardDescription>Content from Creative Hub linked to this campaign</CardDescription>
              </CardHeader>
              <CardContent>
                {linkedContent.length > 0 ? (
                  <div className="space-y-3">
                    {linkedContent.map(content => (
                      <div key={content.id} className="p-3 bg-slate-50 rounded-lg border flex items-start justify-between">
                        <div>
                          <p className="font-medium">{content.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs capitalize">{content.content_type?.replace('_', ' ')}</Badge>
                            <Badge variant="secondary" className="text-xs">{content.status}</Badge>
                          </div>
                        </div>
                        <Link to="/marketing/creative">
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Palette className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No content linked from Creative Hub</p>
                    <Link to="/marketing/creative">
                      <Button variant="link">Go to Creative Hub</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Campaign Attachments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaign.attachments && campaign.attachments.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {campaign.attachments.map((file, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg border text-center">
                        <FileText className="h-8 w-8 mx-auto text-slate-500 mb-2" />
                        <p className="text-sm font-medium truncate">{file.name || file}</p>
                        <Button variant="ghost" size="sm" className="mt-2">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Upload className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="mb-2">No attachments yet</p>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Files
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* A/B Testing Tab */}
        <TabsContent value="testing" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FlaskConical className="h-5 w-5" />
                    A/B Tests
                  </CardTitle>
                  <CardDescription>Experiments linked to this campaign</CardDescription>
                </div>
                <Link to="/marketing/ab-testing">
                  <Button variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Test
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {abTests.length > 0 ? (
                <div className="space-y-4">
                  {abTests.map(test => (
                    <div key={test.id} className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{test.name}</p>
                          <p className="text-xs text-muted-foreground">{test.content_type}</p>
                        </div>
                        <Badge variant={test.status === 'completed' ? 'default' : test.status === 'active' ? 'secondary' : 'outline'}>
                          {test.status}
                        </Badge>
                      </div>
                      {test.variations && (
                        <div className="space-y-2">
                          {test.variations.map((v, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded">
                              <span>{v.name} {v.is_control && <Badge variant="outline" className="text-xs ml-1">Control</Badge>}</span>
                              <span className="font-medium">{v.conversion_rate?.toFixed(1) || 0}% CVR</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {test.status === 'completed' && test.winner_variation && (
                        <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-sm text-green-700 flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            Winner: {test.winner_variation}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="mb-2">No A/B tests linked to this campaign</p>
                  <p className="text-sm mb-4">Create tests to optimize your campaign performance</p>
                  <Link to="/marketing/ab-testing">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create A/B Test
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Eye className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold">{campaign.impressions?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Impressions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <MousePointer className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold">{campaign.clicks?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Clicks</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Target className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold">{campaign.conversions?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Conversions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold">${campaign.spent?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Spent</p>
              </CardContent>
            </Card>
          </div>
          
          {['live', 'completed'].includes(campaign.status) ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Click-Through Rate</span>
                    <span className="font-bold">{campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(2) : 0}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Conversion Rate</span>
                    <span className="font-bold">{campaign.clicks > 0 ? ((campaign.conversions / campaign.clicks) * 100).toFixed(2) : 0}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Cost Per Click</span>
                    <span className="font-bold">${campaign.clicks > 0 ? (campaign.spent / campaign.clicks).toFixed(2) : 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Cost Per Conversion</span>
                    <span className="font-bold">${campaign.conversions > 0 ? (campaign.spent / campaign.conversions).toFixed(2) : 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="mb-2">Results will appear when campaign is live</p>
                <p className="text-sm">Campaign Status: {statusConfig.label}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="h-5 w-5" />
                Workflow History
              </CardTitle>
              <CardDescription>Timeline of all actions and status changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Legal Reviews */}
                {legalReviews.length > 0 && legalReviews.map((review, idx) => (
                  <div key={`review-${idx}`} className={`p-4 rounded-lg border ${review.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Scale className={`h-4 w-4 ${review.passed ? 'text-green-600' : 'text-red-600'}`} />
                        <span className="font-medium">Legal Review</span>
                      </div>
                      <Badge variant={review.passed ? 'default' : 'destructive'}>{review.overall_score?.toFixed(0)}%</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(review.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                    {review.suggestions?.length > 0 && (
                      <div className="mt-2 text-sm">
                        <p className="font-medium">Suggestions:</p>
                        <ul className="list-disc list-inside">
                          {review.suggestions.slice(0, 3).map((s, i) => (
                            <li key={i} className="text-muted-foreground">{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Revisions */}
                {revisions.length > 0 && revisions.map((revision, idx) => (
                  <div key={`revision-${idx}`} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <RotateCcw className="h-4 w-4 text-orange-600" />
                        <span className="font-medium">Revision Requested</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {revision.created_at && format(new Date(revision.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                    <p className="text-sm">{revision.reason}</p>
                    {revision.specific_changes?.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-sm text-muted-foreground">
                        {revision.specific_changes.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
                
                {/* Campaign Creation */}
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-slate-600" />
                      <span className="font-medium">Campaign Created</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {campaign.created_at && format(new Date(campaign.created_at), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                </div>
                
                {legalReviews.length === 0 && revisions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No workflow history yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discussion Tab */}
        <TabsContent value="discussion" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Discussion ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Comment */}
              <div className="flex gap-3">
                <Textarea
                  placeholder="Add a comment or feedback..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  className="flex-1"
                />
                <Button onClick={handleAddComment} disabled={submittingComment || !newComment.trim()}>
                  {submittingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              
              <Separator />
              
              {/* Comments List */}
              {comments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No comments yet</p>
                  <p className="text-sm">Start the discussion by adding a comment</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{comment.author_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <p>{comment.content}</p>
                        
                        {/* Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="mt-3 pl-4 border-l-2 border-slate-200 space-y-2">
                            {comment.replies.map((reply) => (
                              <div key={reply.id} className="p-2 bg-white rounded">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm">{reply.author_name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(reply.created_at), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                <p className="text-sm">{reply.content}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Campaign</DialogTitle>
            <DialogDescription>
              Provide your decision on this campaign
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any feedback or notes..."
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setRevisionDialog(true)}
              disabled={processingApproval}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Request Revision
            </Button>
            <Button
              variant="destructive"
              onClick={handleDirectReject}
              disabled={processingApproval}
            >
              <ThumbsDown className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={handleDirectApprove}
              disabled={processingApproval}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingApproval ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ThumbsUp className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision Request Dialog */}
      <Dialog open={revisionDialog} onOpenChange={setRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
            <DialogDescription>
              Specify what changes are needed before this campaign can be approved
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Revision *</Label>
              <Textarea
                placeholder="Explain why revisions are needed..."
                value={revisionReason}
                onChange={(e) => setRevisionReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Specific Changes (one per line)</Label>
              <Textarea
                placeholder="List specific changes needed...&#10;- Update headline&#10;- Fix budget calculation&#10;- Add more details to description"
                value={revisionChanges}
                onChange={(e) => setRevisionChanges(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestRevision} 
              disabled={processingApproval}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processingApproval ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Request Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Media Publish Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Publish to Social Media
            </DialogTitle>
            <DialogDescription>
              Share this campaign across your connected social media platforms
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Platform Selection */}
            <div className="space-y-3">
              <Label>Select Platforms</Label>
              <div className="grid grid-cols-3 gap-3">
                {socialStatus.connected?.facebook && (
                  <div 
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPlatforms.includes('facebook') 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => togglePlatform('facebook')}
                    data-testid="platform-facebook"
                  >
                    <Facebook className={`h-6 w-6 ${selectedPlatforms.includes('facebook') ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span className="text-sm">Facebook</span>
                    {selectedPlatforms.includes('facebook') && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                  </div>
                )}
                {socialStatus.connected?.twitter && (
                  <div 
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPlatforms.includes('twitter') 
                        ? 'border-black bg-slate-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => togglePlatform('twitter')}
                    data-testid="platform-twitter"
                  >
                    <Twitter className={`h-6 w-6 ${selectedPlatforms.includes('twitter') ? 'text-black' : 'text-slate-400'}`} />
                    <span className="text-sm">Twitter/X</span>
                    {selectedPlatforms.includes('twitter') && <CheckCircle2 className="h-4 w-4 text-black" />}
                  </div>
                )}
                {socialStatus.connected?.linkedin && (
                  <div 
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPlatforms.includes('linkedin') 
                        ? 'border-blue-700 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => togglePlatform('linkedin')}
                    data-testid="platform-linkedin"
                  >
                    <Linkedin className={`h-6 w-6 ${selectedPlatforms.includes('linkedin') ? 'text-blue-700' : 'text-slate-400'}`} />
                    <span className="text-sm">LinkedIn</span>
                    {selectedPlatforms.includes('linkedin') && <CheckCircle2 className="h-4 w-4 text-blue-700" />}
                  </div>
                )}
              </div>
              {!socialStatus.any_connected && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No social media accounts connected.{' '}
                    <Link to="/settings/social-media" className="text-blue-600 hover:underline">
                      Connect accounts
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Post Content */}
            <div className="space-y-2">
              <Label>Post Content</Label>
              <Textarea
                placeholder="Write your social media post..."
                value={publishContent}
                onChange={(e) => setPublishContent(e.target.value)}
                rows={4}
                maxLength={280}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Character count: {publishContent.length}</span>
                <span>{280 - publishContent.length} remaining (Twitter limit)</span>
              </div>
            </div>

            {/* Link URL */}
            <div className="space-y-2">
              <Label>Link URL (optional)</Label>
              <Input
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>

            {/* Preview */}
            {publishContent && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <p className="text-sm">{publishContent}</p>
                {linkUrl && (
                  <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-2">
                    <ExternalLink className="h-3 w-3" /> {linkUrl}
                  </a>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePublishToSocial}
              disabled={publishing || selectedPlatforms.length === 0 || !publishContent.trim()}
              data-testid="confirm-publish-btn"
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Publish Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Change Campaign Status
            </DialogTitle>
            <DialogDescription>
              Select a new status for this campaign
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select 
                value={selectedStatus} 
                onValueChange={setSelectedStatus}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  {WORKFLOW_STAGES.map(stage => {
                    const StageIcon = stage.icon;
                    return (
                      <SelectItem key={stage.id} value={stage.id}>
                        <div className="flex items-center gap-2">
                          <StageIcon className="h-4 w-4" />
                          {stage.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="paused">
                    <div className="flex items-center gap-2">
                      <Pause className="h-4 w-4" />
                      Paused
                    </div>
                  </SelectItem>
                  <SelectItem value="completed">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Completed
                    </div>
                  </SelectItem>
                  <SelectItem value="rejected">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Rejected
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                placeholder="Add a note about this status change..."
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStatusDialog(false);
              setSelectedStatus('');
              setStatusNote('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleStatusChange(selectedStatus)}
              disabled={updatingStatus || !selectedStatus}
              data-testid="confirm-status-change-btn"
            >
              {updatingStatus ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Campaign Task</DialogTitle>
            <DialogDescription>
              Add a task for this campaign and assign to a team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
                data-testid="campaign-task-title-input"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <Select 
                  value={taskForm.assigned_to || 'unassigned'} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
                >
                  <SelectTrigger data-testid="campaign-task-assignee-select">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="task-due">Due Date</Label>
              <Input
                id="task-due"
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={creatingTask} data-testid="create-campaign-task-submit">
              {creatingTask ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignDetailPage;
